import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { states } from "@/data/states";
import { investorCities } from "@/data/investor-cities";
import { fipsForAbbrev } from "./state-fips";

/**
 * Market Stats pipeline — free U.S. Census ACS 5-Year data.
 *
 * - `getMarketStats({stateSlug, citySlug?})` reads cached row(s) from
 *   `market_stats` (public read via anon).
 * - `refreshAllMarketStats()` pulls the latest ACS 5-Year "place" data per
 *   state, matches against curated investor cities + a state-level row,
 *   estimates a cap rate from median rent / median home value, and upserts.
 *
 * Variables pulled (ACS 5-Year, table B25/B01/B19):
 *   B25077_001E  median home value (owner-occupied)
 *   B25064_001E  median gross rent
 *   B01003_001E  total population
 *   B19013_001E  median household income
 *   B25002_002E  occupied housing units
 *   B25002_003E  vacant housing units
 *
 * No API key is required for light usage. If CENSUS_API_KEY is set it will
 * be included to lift rate limits.
 */

const ACS_YEAR = "2023"; // ACS 5-Year 2019-2023 (most recent published)

export type MarketStats = {
  stateSlug: string;
  citySlug: string | null;
  cityName: string | null;
  medianHomeValue: number | null;
  medianRent: number | null;
  medianRent2BR: number | null;
  medianRent3BR: number | null;
  population: number | null;
  medianIncome: number | null;
  vacancyRate: number | null;
  capRateEstimate: number | null;
  unemploymentRate: number | null;
  unemploymentPeriod: string | null;
  vintage: string | null;
  refreshedAt: string;
};

// ---------- Read (public) ----------

function browserSafeClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient(url, key, {
    auth: { persistSession: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

const readSchema = z.object({
  stateSlug: z.string().min(1).max(60),
  citySlug: z.string().min(1).max(80).nullable().optional(),
});

function rowToStats(r: Record<string, unknown>): MarketStats {
  return {
    stateSlug: String(r.state_slug),
    citySlug: (r.city_slug as string | null) ?? null,
    cityName: (r.city_name as string | null) ?? null,
    medianHomeValue: (r.median_home_value as number | null) ?? null,
    medianRent: (r.median_rent as number | null) ?? null,
    medianRent2BR: (r.median_rent_2br as number | null) ?? null,
    medianRent3BR: (r.median_rent_3br as number | null) ?? null,
    population: (r.population as number | null) ?? null,
    medianIncome: (r.median_income as number | null) ?? null,
    vacancyRate: r.vacancy_rate != null ? Number(r.vacancy_rate) : null,
    capRateEstimate: r.cap_rate_estimate != null ? Number(r.cap_rate_estimate) : null,
    unemploymentRate: r.unemployment_rate != null ? Number(r.unemployment_rate) : null,
    unemploymentPeriod: (r.unemployment_period as string | null) ?? null,
    vintage: (r.vintage as string | null) ?? null,
    refreshedAt: String(r.refreshed_at),
  };
}

export const getMarketStats = createServerFn({ method: "GET" })
  .inputValidator((d) => readSchema.parse(d))
  .handler(async ({ data }): Promise<MarketStats | null> => {
    const sb = browserSafeClient();
    const q = sb
      .from("market_stats")
      .select("*")
      .eq("state_slug", data.stateSlug)
      .limit(1);
    const { data: rows, error } = data.citySlug
      ? await q.eq("city_slug", data.citySlug)
      : await q.is("city_slug", null);
    if (error) {
      console.error("getMarketStats:", error.message);
      return null;
    }
    if (!rows || rows.length === 0) return null;
    return rowToStats(rows[0] as Record<string, unknown>);
  });

// ---------- Refresh ----------

// Census NAME (city part, before the comma) for cities whose place name
// doesn't normalize cleanly against our curated list. ACS 5-Year 2023.
const CENSUS_NAME_OVERRIDES: Record<string, string> = {
  "hawaii/honolulu": "Urban Honolulu CDP",
  "hawaii/kailua-kona": "Kailua CDP (Hawaii County)",
  "kentucky/lexington": "Lexington-Fayette urban county",
  "tennessee/nashville": "Nashville-Davidson metropolitan government (balance)",
};

function normalizeName(s: string): string {
  return s
    .toLowerCase()
    .replace(/\bmetro(politan)?\s+government\b/g, "")
    .replace(/\bmetro(politan)?\s+(area|division)\b/g, "")
    .replace(/\bconsolidated\s+government\b/g, "")
    .replace(/\((balance|remainder|part)\)/g, "")
    .replace(/\bunified\s+government\b/g, "")
    .replace(/\/[a-z\s]+/g, "") // strip "/jefferson county"
    .replace(/-[a-z]+$/g, "") // strip "-davidson"
    .replace(/\b(city|town|village|cdp|borough|municipality|urban county|county)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

type CensusRow = {
  name: string;
  place: string;
  medianHomeValue: number | null;
  medianRent: number | null;
  medianRent2BR: number | null;
  medianRent3BR: number | null;
  population: number | null;
  medianIncome: number | null;
  occupied: number | null;
  vacant: number | null;
};

const CENSUS_VARS = [
  "NAME",
  "B25077_001E", // median home value
  "B25064_001E", // median gross rent
  "B25031_004E", // median 2BR contract rent
  "B25031_005E", // median 3BR contract rent
  "B01003_001E", // population
  "B19013_001E", // median household income
  "B25002_002E", // occupied
  "B25002_003E", // vacant
];

function parseCensus(header: string[], body: string[]): CensusRow {
  const idx = Object.fromEntries(header.map((h, i) => [h, i])) as Record<string, number>;
  const num = (v: string | undefined) => {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) return null;
    return n;
  };
  return {
    name: body[idx.NAME] ?? "",
    place: body[idx.place] ?? "",
    medianHomeValue: num(body[idx.B25077_001E]),
    medianRent: num(body[idx.B25064_001E]),
    medianRent2BR: num(body[idx.B25031_004E]),
    medianRent3BR: num(body[idx.B25031_005E]),
    population: num(body[idx.B01003_001E]),
    medianIncome: num(body[idx.B19013_001E]),
    occupied: num(body[idx.B25002_002E]),
    vacant: num(body[idx.B25002_003E]),
  };
}

async function fetchCensusPlaces(stateFips: string): Promise<CensusRow[]> {
  const key = process.env.CENSUS_API_KEY;
  const url =
    `https://api.census.gov/data/${ACS_YEAR}/acs/acs5` +
    `?get=${CENSUS_VARS.join(",")}&for=place:*&in=state:${stateFips}` +
    (key ? `&key=${encodeURIComponent(key)}` : "");
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Census ${stateFips} ${res.status}: ${await res.text()}`);
  const rows = (await res.json()) as string[][];
  if (!Array.isArray(rows) || rows.length < 2) return [];
  const [header, ...body] = rows;
  return body.map((r) => parseCensus(header, r));
}

async function fetchCensusState(stateFips: string): Promise<CensusRow | null> {
  const key = process.env.CENSUS_API_KEY;
  const url =
    `https://api.census.gov/data/${ACS_YEAR}/acs/acs5` +
    `?get=${CENSUS_VARS.join(",")}&for=state:${stateFips}` +
    (key ? `&key=${encodeURIComponent(key)}` : "");
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Census state ${stateFips} ${res.status}`);
  const rows = (await res.json()) as string[][];
  if (!Array.isArray(rows) || rows.length < 2) return null;
  return parseCensus(rows[0], rows[1]);
}

function computeCapRate(rent: number | null, home: number | null): number | null {
  if (!rent || !home || home <= 0) return null;
  // Standard rule-of-thumb: NOI ≈ (rent * 12) * 0.60 (40% expense ratio)
  return Math.round(((rent * 12 * 0.6) / home) * 1000) / 10; // percent, one decimal
}

function computeVacancy(occupied: number | null, vacant: number | null): number | null {
  if (!occupied || vacant == null) return null;
  const total = occupied + vacant;
  if (!total) return null;
  return Math.round((vacant / total) * 1000) / 10;
}

type UpsertRow = {
  state_slug: string;
  city_slug: string | null;
  city_name: string | null;
  state_abbrev: string | null;
  median_home_value: number | null;
  median_rent: number | null;
  median_rent_2br: number | null;
  median_rent_3br: number | null;
  population: number | null;
  median_income: number | null;
  vacancy_rate: number | null;
  cap_rate_estimate: number | null;
  unemployment_rate: number | null;
  unemployment_period: string | null;
  vintage: string;
  refreshed_at: string;
};

function normalizeCounty(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .toLowerCase()
    .replace(/,.*$/, "") // drop state suffix from Census NAME
    .replace(/\b(county|parish|borough|municipality|city and borough|census area|city)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

async function fetchCountyFipsMap(stateFips: string): Promise<Map<string, string>> {
  const key = process.env.CENSUS_API_KEY;
  const url =
    `https://api.census.gov/data/${ACS_YEAR}/acs/acs5?get=NAME&for=county:*&in=state:${stateFips}` +
    (key ? `&key=${encodeURIComponent(key)}` : "");
  const res = await fetch(url);
  if (!res.ok) return new Map();
  const rows = (await res.json()) as string[][];
  const map = new Map<string, string>();
  for (const r of rows.slice(1)) {
    const [name, , countyFips] = r;
    if (!name || !countyFips) continue;
    map.set(normalizeCounty(name), countyFips);
  }
  return map;
}

export async function refreshAllMarketStats(): Promise<{
  ok: true;
  stateRows: number;
  cityRows: number;
  unmatched: string[];
  cityLausMatched: number;
}> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { fetchStateUnemployment, fetchCountyUnemployment } = await import("./bls.functions");
  const now = new Date().toISOString();
  const upserts: UpsertRow[] = [];
  const unmatched: string[] = [];
  let stateRows = 0;
  let cityRows = 0;
  let cityLausMatched = 0;

  // Fetch BLS unemployment for all service-area states in batches (one call per 25).
  const abbrevs = states.map((s) => s.abbrev);
  const unemploymentByAbbrev = await fetchStateUnemployment(abbrevs).catch((e) => {
    console.error("BLS unemployment fetch failed:", (e as Error).message);
    return {} as Record<string, { rate: number | null; period: string | null }>;
  });

  // ---- Resolve county FIPS per investor city (via Census), then batch fetch
  // county-level LAUS unemployment rates keyed by `${stateFips}${countyFips}`.
  const cityCountyKey = new Map<string, string>(); // "state/city" -> "SSCCC"
  const countyLookups: Array<{ stateFips: string; countyFips: string }> = [];
  for (const state of states) {
    const fips = fipsForAbbrev(state.abbrev);
    if (!fips) continue;
    const citiesInState = investorCities.filter((c) => c.stateSlug === state.slug);
    if (citiesInState.length === 0) continue;
    const countyMap = await fetchCountyFipsMap(fips).catch(() => new Map<string, string>());
    for (const c of citiesInState) {
      if (!c.county) continue;
      const countyFips = countyMap.get(normalizeCounty(c.county));
      if (!countyFips) continue;
      const key = `${fips}${countyFips}`;
      cityCountyKey.set(`${c.stateSlug}/${c.slug}`, key);
      countyLookups.push({ stateFips: fips, countyFips });
    }
  }
  const countyLaus = await fetchCountyUnemployment(countyLookups).catch((e) => {
    console.error("BLS county unemployment failed:", (e as Error).message);
    return {} as Record<string, { rate: number | null; period: string | null }>;
  });

  for (const state of states) {
    const fips = fipsForAbbrev(state.abbrev);
    if (!fips) continue;
    const laus = unemploymentByAbbrev[state.abbrev] ?? null;

    // State-level row
    try {
      const s = await fetchCensusState(fips);
      if (s) {
        upserts.push({
          state_slug: state.slug,
          city_slug: null,
          city_name: null,
          state_abbrev: state.abbrev,
          median_home_value: s.medianHomeValue,
          median_rent: s.medianRent,
          median_rent_2br: s.medianRent2BR,
          median_rent_3br: s.medianRent3BR,
          population: s.population,
          median_income: s.medianIncome,
          vacancy_rate: computeVacancy(s.occupied, s.vacant),
          cap_rate_estimate: computeCapRate(s.medianRent, s.medianHomeValue),
          unemployment_rate: laus?.rate ?? null,
          unemployment_period: laus?.period ?? null,
          vintage: `ACS 5-Year ${ACS_YEAR}`,
          refreshed_at: now,
        });
        stateRows += 1;
      }
    } catch (e) {
      console.error(`state ${state.slug}:`, e instanceof Error ? e.message : e);
    }

    // Cities in this state
    const citiesInState = investorCities.filter((c) => c.stateSlug === state.slug);
    if (citiesInState.length === 0) continue;

    try {
      const places = await fetchCensusPlaces(fips);
      const normIndex = new Map<string, CensusRow>();
      const rawIndex = new Map<string, CensusRow>();
      for (const p of places) {
        const cityPart = p.name.split(",")[0] ?? p.name;
        normIndex.set(normalizeName(cityPart), p);
        rawIndex.set(cityPart.toLowerCase().trim(), p);
      }
      for (const c of citiesInState) {
        const overrideKey = `${c.stateSlug}/${c.slug}`;
        const override = CENSUS_NAME_OVERRIDES[overrideKey];
        const match = override
          ? (rawIndex.get(override.toLowerCase()) ?? normIndex.get(normalizeName(override)))
          : normIndex.get(normalizeName(c.name));
        if (!match) {
          unmatched.push(overrideKey);
          continue;
        }
        // City-level (county LAUS) rate when we can resolve county FIPS,
        // otherwise fall back to the statewide rate.
        const countyKey = cityCountyKey.get(overrideKey);
        const cityLaus = countyKey ? countyLaus[countyKey] : undefined;
        if (cityLaus?.rate != null) cityLausMatched += 1;
        upserts.push({
          state_slug: c.stateSlug,
          city_slug: c.slug,
          city_name: c.name,
          state_abbrev: state.abbrev,
          median_home_value: match.medianHomeValue,
          median_rent: match.medianRent,
          median_rent_2br: match.medianRent2BR,
          median_rent_3br: match.medianRent3BR,
          population: match.population,
          median_income: match.medianIncome,
          vacancy_rate: computeVacancy(match.occupied, match.vacant),
          cap_rate_estimate: computeCapRate(match.medianRent, match.medianHomeValue),
          unemployment_rate: cityLaus?.rate ?? laus?.rate ?? null,
          unemployment_period: cityLaus?.period ?? laus?.period ?? null,
          vintage: `ACS 5-Year ${ACS_YEAR}`,
          refreshed_at: now,
        });
        cityRows += 1;
      }
    } catch (e) {
      console.error(`places ${state.slug}:`, e instanceof Error ? e.message : e);
    }
  }

  const CHUNK = 200;
  for (let i = 0; i < upserts.length; i += CHUNK) {
    const batch = upserts.slice(i, i + CHUNK);
    const { error } = await supabaseAdmin
      .from("market_stats")
      .upsert(batch, { onConflict: "state_slug,city_slug" });
    if (error) throw new Error(`upsert failed: ${error.message}`);
  }

  return { ok: true, stateRows, cityRows, unmatched, cityLausMatched };
}

